/**
 * Table des repères de page de l'espace électricien : titre + ancêtres.
 *
 * Source UNIQUE, partagée par les deux rendus :
 *  - desktop (`≥ lg`) → dans l'en-tête, titre + fil en une ligne de texte,
 *    tel qu'il a toujours été ;
 *  - mobile (`< lg`) → `PageHeading`, en tête du contenu, où le fil devient
 *    cliquable et ne se tronque plus.
 *
 * Le tableau de bord est la RACINE de l'espace pro (page d'atterrissage après
 * connexion) : tous les fils partent de lui, plus de « Page d'accueil ».
 */

export interface Crumb {
  /** Clé i18n + libellé FR de repli, comme partout ailleurs dans l'app. */
  key: string;
  fallback: string;
  /** Absent = maillon non cliquable (nœud de regroupement du menu). */
  to?: string;
}

export interface PageMeta {
  /** Titre de la page — aussi le dernier maillon du fil. */
  title: Crumb;
  /** Ancêtres uniquement ; sans ancêtre, pas de fil du tout. */
  parents: Crumb[];
}

const DASHBOARD: Crumb = {
  key: 'pro.sidebar.dashboard',
  fallback: 'Tableau de bord',
  to: '/tableau-de-bord',
};

// « Mes commandes » n'a pas de page à elle : c'est un groupe du menu, donc un
// maillon inerte.
const ORDERS: Crumb = { key: 'pro.sidebar.orders', fallback: 'Mes commandes' };

// Idem pour le panier : c'est un tiroir, pas une route.
const CART: Crumb = { key: 'pro.header.cart', fallback: 'Panier' };

const PAGE_META: Record<string, PageMeta> = {
  '/tableau-de-bord': { title: DASHBOARD, parents: [] },
  '/home': {
    title: { key: 'pro.sidebar.shop', fallback: 'Boutique' },
    parents: [DASHBOARD],
  },
  '/devis': {
    title: { key: 'pro.sidebar.devis', fallback: 'Devis' },
    parents: [DASHBOARD, ORDERS],
  },
  '/ordres': {
    title: { key: 'pro.sidebar.ordres', fallback: 'Ordres' },
    parents: [DASHBOARD, ORDERS],
  },
  '/clients': {
    title: { key: 'pro.sidebar.clients', fallback: 'Clients' },
    parents: [DASHBOARD],
  },
  '/catalogue': {
    title: { key: 'pro.sidebar.catalogue', fallback: 'Catalogue' },
    parents: [DASHBOARD],
  },
  '/portefeuille': {
    title: { key: 'pro.sidebar.portefeuille', fallback: 'Portefeuille' },
    parents: [DASHBOARD],
  },
  '/statistique': {
    title: { key: 'pro.sidebar.statistique', fallback: 'Statistique' },
    parents: [DASHBOARD],
  },
  '/settings': {
    title: { key: 'pro.sidebar.settings', fallback: 'Paramètres' },
    parents: [DASHBOARD],
  },
  '/checkout': {
    title: { key: 'pro.page.checkout', fallback: 'Validation de commande' },
    parents: [DASHBOARD, CART],
  },
};

/** Préfixe le plus long d'abord : `/devis/ABC` retombe sur `/devis`. */
const SORTED_ROUTES = Object.keys(PAGE_META).sort((a, b) => b.length - a.length);

/** `null` pour une route hors table (la formation et la planche des
 *  notifications portent déjà leur propre titre). */
export function getPageMeta(pathname: string): PageMeta | null {
  const match = SORTED_ROUTES.find(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
  return match ? PAGE_META[match]! : null;
}
